<template>
    <div class="vue-reservation-step-2">
        <div class="background-wrapper">
            <div class="row">
                <div class="col-12">
                    <div class="rental-options-wrapper" v-if="hasRentalOptions">
                        <h2 class="virtual-h3">{{ translations.extraOptions }}</h2>

                        <aut-rental-option
                            v-for="rentalOption in rentalOptions"
                            :key="rentalOption.tid"
                            :option="rentalOption"
                            :isBusiness="isBusiness"
                        ></aut-rental-option>
                    </div>

                    <div v-if="!hasRentalOptions">
                        <p>Er zijn geen extra opties beschikbaar</p>
                    </div>

                    <div class="upgrade-wrapper" v-if="hasVehicleUpgrade && !hasVehicleUpgrades">
                        <h3>{{ translations.upgradeLabel }}</h3>

                        <aut-up-or-downgrade-vehicle-card
                            grade-type="upgrade"
                            :vehicle="getVehicleUpgrade"
                            :active-vehicle="activeUpgrade"
                            @card-clicked="handleUpgradeClicked"
                        ></aut-up-or-downgrade-vehicle-card>
                    </div>

                    <div class="upgrade-wrapper" v-if="hasVehicleUpgrades">
                        <h3>{{ translations.upgradeLabel }}</h3>

                        <aut-up-or-downgrade-vehicle-card
                            v-for="vehicleUpgrade in getVehicleUpgrades.slice(0,1)"
                            grade-type="upgrade"
                            :vehicle="vehicleUpgrade"
                            :key="vehicleUpgrade.id"
                            :active-vehicle="activeUpgrade"
                            @card-clicked="handleUpgradeClicked"
                        ></aut-up-or-downgrade-vehicle-card>
                    </div>
                </div>
            </div>
        </div>

      <div class="button-wrapper-mobile row">
        <div class="col-12 d-flex justify-content-between">
          <router-link class="btn-ghost btn-no-arrow"
                       :to="{ name: this.isBusiness ? 'stepOneBusiness' : 'stepOne', query: { vehicleId: getActiveVehicle.id, locationId: getActiveLocation.id } }">
            {{ translations.backRentalPeriodText }}
          </router-link>
          </div>
        <div class="col-12 d-flex justify-content-between">
          <router-link class="btn-primary me-0"
                       :to="{ name: this.isBusiness ? 'stepThreeBusiness' : 'stepThree', query: { vehicleId: getActiveVehicle.id, locationId: getActiveLocation.id } }">
            {{ translations.nextDataText }}
          </router-link>
        </div>
      </div>

        <div class="button-wrapper row">
            <div class="col-12 d-flex justify-content-between">
                <router-link class="btn-ghost btn-no-arrow"
                             :to="{ name: this.isBusiness ? 'stepOneBusiness' : 'stepOne', query: { vehicleId: getActiveVehicle.id, locationId: getActiveLocation.id, vehicleWrapperId: getActiveVehicleWrapper.vehicle_wrapper_id } }">
                    {{ translations.backText }}
                </router-link>

                <div class="d-flex mb-0 total-price-button mr-1">
                    <div class="mr-auto p-2 price-label align-middle">{{ translations.rentalPrice }}<br>
                    <a href="#receipt" class="receipt-link">{{ translations.checkReceipt }}</a></div>
                    <div class="p-2 price-total">{{ getTotalRentalPrice | vatExcluder | currencyFormatter }}</div>
                </div>

                <router-link class="btn-primary me-0"
                             :to="{ name: this.isBusiness ? 'stepThreeBusiness' : 'stepThree', query: { vehicleId: getActiveVehicle.id, locationId: getActiveLocation.id, vehicleWrapperId: getActiveVehicleWrapper.vehicle_wrapper_id } }">
                    {{ translations.nextText }}
                </router-link>
            </div>

            <div class="col-12 text-end opacity-50 additional-text">
                <small>{{ translations.weOnlyNeedAdditionalInformation }}</small>
            </div>
        </div>
    </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import RouterLinks from "./RouterLinks";
import translations from "../../mixins/translations";
import RentalOption from "./RentalOption";
import CurrencyFormatter from "../../mixins/CurrencyFormatter";
import UpOrDowngradeVehicleCard from "./UpOrDowngradeVehicleCard";

export default {
    name: "ReservationStepTwo",

    mixins: [translations, CurrencyFormatter],

    components: {
        'aut-router-links': RouterLinks,
        'aut-rental-option': RentalOption,
        'aut-up-or-downgrade-vehicle-card': UpOrDowngradeVehicleCard,
    },

    data() {
        return {
            isBusiness: null,
            vehicleId: null,
            locationId: null,
            activeUpgradeId: null,
            originalVehicleId: null,
        }
    },

    computed: {
        ...mapGetters([
            'getActiveVehicle',
            'getActiveLocation',
            'getRentalOptions',
            'getVehicleUpgrade',
            'getVehicleUpgrades',
            'getVehicleDowngrade',
            'getVehicleUpgrade',
            'getVehicleDowngrade',
            'hasRentalOptions',
            'getTotalRentalPrice',
            'getItemsForGA4',
            'getStartDate',
            'getEndDate',
            'getActiveVehicleWrapper',
        ]),

        hasVehicleUpgrade() {
            return !lodash.isNil(this.getVehicleUpgrade);
        },

        hasVehicleUpgrades() {
            return !lodash.isEmpty(this.getVehicleUpgrades);
        },

        hasVehicleDowngrade() {
            return !lodash.isNil(this.getVehicleDowngrade);
        },

        rentalOptions() {
            if (this.isBusiness) {
                return this.getRentalOptions.filter((rentalOption) => {
                    return rentalOption.flowType === 'zakelijk' || rentalOption.flowType === 'beide';
                });
            }

            return this.getRentalOptions.filter((rentalOption) => {
              return rentalOption.flowType === 'consument' || rentalOption.flowType === 'beide';
            });
        },

        activeUpgrade() {
            if (lodash.isNil(this.activeUpgradeId)) {
                return null;
            }

            let vehicle = this.getVehicleUpgrades.find((vehicle) => {
                return vehicle.id === this.activeUpgradeId;
            })

            if (lodash.isNil(vehicle)) {
                return this.getVehicleUpgrade;
            }

            return vehicle;
        }
    },

    created() {
        this.isBusiness = this.$route.meta.isBusiness ?? false;
        this.vehicleId = this.$route.query.vehicleId

        if (!lodash.isNil(this.$route.query.locationId)) {
            this.locationId = this.$route.query.locationId
        }

        this.setCurrentStep(this.$route.name);
        this.setRentalOptions();

        const urlParams = new URLSearchParams(window.location.search);
        this.originalVehicleId = urlParams.get('vehicleId');
    },

    mounted() {
        window.dataLayer.push({ecommerce: null});
        window.dataLayer.push({
            'event': 'add_payment_info',
            'ecommerce': {
                'currency': 'EUR',
                'value': this.getTotalRentalPrice,
                'items': this.getItemsForGA4,
                'retrieve_date': this.getStartDate,
                'handin_date': this.getEndDate,
            },
        });
    },

    methods: {
        ...mapActions([
            'setCurrentStep',
            'setRentalOptions',
            'resetActiveRentalOptions',
            'changeVehicle',
        ]),

        handleUpgradeClicked(payload) {
            if (this.activeUpgradeId === payload) {
                this.activeUpgradeId = null;

                this.changeVehicle({
                    vehicleId: this.originalVehicleId,
                    isUpgrade: true,
                    isBusiness: this.isBusiness
                });
                return;
            }

            this.activeUpgradeId = payload;

            this.changeVehicle({
                vehicleId: this.activeUpgradeId,
                isUpgrade: true,
                isBusiness: this.isBusiness
            });
        }
    }
}
</script>
